/**
 * G12. Увольнение/кризис: «Собрать месяц». При событии `work.layoff` или
 * мировом кризисе (`flags.worldCrisis`, ставит мировой слой, план 4–5) —
 * бюджет из шести статей, вычеркнуть две. Что вычеркнул — то и будет:
 * еда → здоровье −5, лечение → здоровье −3, школа детям → `flags.childBond`
 * −1 (без детей вместо школы — свои курсы: знания −2), жильё → отношения −2
 * (съехать к родным), транспорт → счастье −1, отдых → счастье −2 [моё].
 * Суммы на экране — доля месячного дохода (работа; без работы — ВВП на душу
 * как прокси), честно в долларах ППС. «Пропустить» — транспорт и отдых.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { FLAG_LAYOFF_YEAR, type Life, type MinigameOutcome, type Stats } from '@life/engine';
import type { MinigameDef } from '../types';
import { yearlyIncome } from '../casino/common';
import { distinctIndexes, nothing, numberFlag } from './common';
import { FLAG_CHILD_BOND } from './talk';

export const BUDGET_TRIGGER_EVENT = 'work.layoff';
/** Мировой кризис: ставит мировой слой (план 4–5), здесь только читается. */
export const FLAG_WORLD_CRISIS = 'worldCrisis';
export const BUDGET_MIN_AGE = 18;
export const BUDGET_MAX_AGE = 100;
export const BUDGET_ITEMS_COUNT = 6;
export const BUDGET_CUTS = 2;
export const BUDGET_ITEMS = ['food', 'rent', 'treatment', 'school', 'study', 'transport', 'leisure'] as const;
export type BudgetItemId = (typeof BUDGET_ITEMS)[number];

export interface BudgetEffect {
  health?: number;
  happiness?: number;
  knowledge?: number;
  relations?: number;
  childBond?: number;
}

/** Доля месячного дохода и эффект вычёркивания. */
export const BUDGET_SHARES: Readonly<Record<BudgetItemId, number>> = {
  food: 0.3,
  rent: 0.35,
  treatment: 0.1,
  school: 0.1,
  study: 0.1,
  transport: 0.08,
  leisure: 0.07,
};
export const BUDGET_EFFECTS: Readonly<Record<BudgetItemId, BudgetEffect>> = {
  food: { health: -5 },
  rent: { relations: -2 },
  treatment: { health: -3 },
  school: { childBond: -1 },
  study: { knowledge: -2 },
  transport: { happiness: -1 },
  leisure: { happiness: -2 },
};
/** Что вычёркивает автопилот. */
export const BUDGET_SKIP_CUTS: readonly BudgetItemId[] = ['transport', 'leisure'];

export interface BudgetItem {
  id: BudgetItemId;
  key: string;
  effectKey: string;
  share: number;
  /** Долларов ППС в месяц. */
  amount: number;
}

export interface BudgetState {
  items: BudgetItem[];
  cuts: number;
  /** Месячный доход в долларах ППС. */
  monthly: number;
}

export type BudgetInput = { picks: number[] };

export function budgetItemKey(id: string): string {
  return `minigame.budget.item.${id}`;
}

export function budgetEffectKey(id: string): string {
  return `minigame.budget.effect.${id}`;
}

/** Статьи бюджета этой жизни: «школа детям» при детях, иначе «свои курсы». */
export function budgetItemIds(life: Life): BudgetItemId[] {
  return ['food', 'rent', 'treatment', life.children > 0 ? 'school' : 'study', 'transport', 'leisure'];
}

function layoffNow(life: Life): boolean {
  const year = life.flags[FLAG_LAYOFF_YEAR];
  if (typeof year !== 'number') return false;
  const now = life.birthYear + life.age;
  return year === now || year === now - 1;
}

function result(life: Life, cut: readonly BudgetItemId[]): MinigameOutcome {
  const stats: Partial<Stats> = {};
  let bond = 0;
  for (const id of cut) {
    const e = BUDGET_EFFECTS[id];
    if (e.health) stats.health = (stats.health ?? 0) + e.health;
    if (e.happiness) stats.happiness = (stats.happiness ?? 0) + e.happiness;
    if (e.knowledge) stats.knowledge = (stats.knowledge ?? 0) + e.knowledge;
    if (e.relations) stats.relations = (stats.relations ?? 0) + e.relations;
    if (e.childBond) bond += e.childBond;
  }
  const outcome: MinigameOutcome = {
    ...nothing(),
    logKey: cut.includes('food') ? 'minigame.budget.hungry' : cut.includes('treatment') ? 'minigame.budget.untreated' : 'minigame.budget.done',
  };
  if (Object.keys(stats).length > 0) outcome.stats = stats;
  if (bond !== 0) outcome.flags = { [FLAG_CHILD_BOND]: numberFlag(life, FLAG_CHILD_BOND) + bond };
  return outcome;
}

export const budget: MinigameDef<BudgetInput, BudgetState> = {
  id: 'budget',
  section: 'G',
  triggers: [
    { kind: 'event', event: BUDGET_TRIGGER_EVENT },
    { kind: 'age', min: BUDGET_MIN_AGE, max: BUDGET_MAX_AGE, everyYears: 1 },
  ],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return layoffNow(life) || life.flags[FLAG_WORLD_CRISIS] === true;
  },
  setup(life: Life, ds: Dataset): BudgetState {
    const monthly = Math.max(0, yearlyIncome(ds, life)) / 12;
    const items = budgetItemIds(life).map((id) => ({ id, key: budgetItemKey(id), effectKey: budgetEffectKey(id), share: BUDGET_SHARES[id], amount: BUDGET_SHARES[id] * monthly }));
    return { items, cuts: BUDGET_CUTS, monthly };
  },
  play(state: BudgetState, input: BudgetInput, life: Life): MinigameOutcome {
    const picks = distinctIndexes(input.picks, state.items.length).slice(0, state.cuts);
    return result(
      life,
      picks.map((i) => state.items[i]!.id),
    );
  },
  expected(state: BudgetState, life: Life): MinigameOutcome {
    const cut = state.items.map((i) => i.id).filter((id) => BUDGET_SKIP_CUTS.includes(id)).slice(0, state.cuts);
    return result(life, cut);
  },
};
